import { createSignal, onCleanup, Show } from 'solid-js';
import { Card, ProgressBar, Spinner } from 'solid-bootstrap';
import { Service } from '../../service/device/setup';
import { detectImageMime } from '../../util/mime/image';
import { putWithProgress } from '../../util/upload/upload';

export const UploadImageCard = (props) => {
  const [file, setFile] = createSignal(null);
  const [previewUrl, setPreviewUrl] = createSignal(null);
  const [status, setStatus] = createSignal('Idle');
  const [progress, setProgress] = createSignal(0);
  const [error, setError] = createSignal(null);
  const [downloadUrl, setDownloadUrl] = createSignal(null);
  const [dragging, setDragging] = createSignal(false);

  function clearPreviewUrl() {
    if (previewUrl() !== null) {
      URL.revokeObjectURL(previewUrl());
    }
  }

  function onFilesSelected(list) {
    if (list === null) {
      return;
    }
    if (list.length === 0) {
      return;
    }

    const first = list[0];
    setFile(first);
    setError(null);
    setDownloadUrl(null);
    setProgress(0);
    setStatus('Ready');

    clearPreviewUrl();
    const url = URL.createObjectURL(first);
    setPreviewUrl(url);

    startUpload()
  }

  async function startUpload() {
    try {
      setStatus('Starting upload…');
      setError(null);
      setProgress(0);
      setStatus('Determining MIME type…');

      const f = file();
      if (f === null) {
        throw new Error('No file selected');
      }

      const mime = await detectImageMime(f);
      const contentType = mime !== null ? mime : 'application/octet-stream';

      setStatus('Creating bucket file…');
      const created = await Service.createBucketFile(props.tag, contentType);
      const uuid = created.uuid;

      setStatus('Getting upload URL…');
      const uploadUrl = await Service.getBucketFileUploadURL(uuid);

      setStatus('Uploading…');
      await putWithProgress(uploadUrl, f, contentType, setProgress);

      if (props.onUploadComplete) {
        props.onUploadComplete(uuid);
      }

      setStatus('Marking upload complete…');
      await Service.markBucketFileUploadComplete(uuid);

      setStatus('Processing…');
      const downloadUrl = await Service.getBucketFileDownloadURL(uuid);
      setDownloadUrl(downloadUrl);
      setStatus('Done');
    } catch (e) {
      if (e && e.message) {
        setError(e.message);
      } else {
        setError(String(e));
      }
      setStatus('Error');
    }
  }

  onCleanup(function () {
    clearPreviewUrl();
  });


  function onDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }


  function onDragEnter(e) {
    e.preventDefault();
    setDragging(true);
  }


  function onDragLeave(e) {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragging(false);
    }
  }


  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFilesSelected(e.dataTransfer.files);
    }
  }

  function isTooManyUserImages() {
    var userImages = props.backgroundImages().filter(img => img.tag === 'user_background_image_thumb');
    return userImages.length >= 10;
  }

  return (
    <Card>
      <Card.Body>
        <Show when={props.backgroundImages.loading}>
          <Spinner animation="border" />
        </Show>
        <Show when={!props.backgroundImages.loading && isTooManyUserImages()}>
          <Card.Title>Upload Image</Card.Title>
          <div class="alert alert-warning" role="alert">
            You have reached the maximum number of background images (10). Please delete some images before uploading new ones.
          </div>
        </Show>
        <Show when={!props.backgroundImages.loading && !isTooManyUserImages()}>
          <Card.Title>Upload Image</Card.Title>
          <div
            class={`border border-2 rounded p-4 text-center mb-3 ${dragging() ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary border-dashed'}`}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            // This function is a little hack to make the whole div clickable.
            onClick={function () {
              const input = document.getElementById('image-file-input');
              if (input !== null) {
                input.click();
              }
            }}
          >
            <p>Drag &amp; drop here, or click to choose</p>
            <input
              id="image-file-input"
              type="file"
              accept="image/*"
              class="d-none"
              onChange={function (e) { onFilesSelected(e.currentTarget.files); }}
            />
          </div>

          <Show when={previewUrl() !== null}>
            <div class="d-flex align-items-center gap-3 mb-3">
              <img src={previewUrl() || ''} alt="preview" style={{ width: '128px', height: '128px', 'object-fit': 'cover', borderRadius: '0.75rem', border: '1px solid #ddd' }} />
              <div class="text-muted small">
                <div><strong>Name:</strong> {file() ? file().name : ''}</div>
                <div><strong>Size:</strong> {file() ? ((file().size / 1024).toFixed(1) + ' KB') : ''}</div>
                <div><strong>Type:</strong> {file() ? (file().type || '(empty)') : ''}</div>
              </div>
            </div>
          </Show>

          <Show when={status() === 'Uploading…' || progress() > 0}>
            <ProgressBar now={progress()} label={progress() + '%'} class="mb-2" />
          </Show>

          <Show when={downloadUrl() !== null}>
            <div class="alert alert-success" role="alert">
              Processed image ready: <a href={downloadUrl() || ''} target="_blank" rel="noreferrer">open</a>
            </div>
          </Show>

          <Show when={error() !== null}>
            <div class="alert alert-danger" role="alert">{error() || ''}</div>
          </Show>
        </Show>
      </Card.Body>
    </Card>
  );
};
